import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';

export function updateNodeEdgeLinks(nodes, allEdges) {
  const sourceEdgesByNodeId = groupBy(allEdges, 'source');
  const targetEdgesByNodeId = groupBy(allEdges, 'target');
  const nodesList = map(nodes, node => ({
    ...node,
    sourceEdges: sourceEdgesByNodeId[node.id] ?? [],
    targetEdges: targetEdgesByNodeId[node.id] ?? [],
  }));

  return keyBy(nodesList, 'id');
}

/**
 * User just dragged a new edge. Add it to Redux as well.
 * @param {Object} nodes
 * @param {Object} newEdge
 * @returns {Object} - new nodes, with edge added to sourceEdges and targetEdges
 */
export function addEdgeToNodes(nodes, newEdge) {
  const sourceNode = nodes[newEdge.source];
  const targetNode = nodes[newEdge.target];

  const newSourceEdges = [...sourceNode.sourceEdges, newEdge];
  const newTargetEdges = [...targetNode.targetEdges, newEdge];

  return {
    ...nodes,
    [sourceNode.id]: {
      ...nodes[sourceNode.id],
      sourceEdges: newSourceEdges,
    },
    [targetNode.id]: {
      ...nodes[targetNode.id],
      targetEdges: newTargetEdges,
    },
  };
}

export function addNewEdge(edges, newEdge) {
  return {
    ...edges,
    [newEdge.id]: newEdge,
  };
}

/**
 * Given the nodes of a graph, and the edgeId of the edge to be deleted:
 *  1. Find the 2 nodes that have this edge as their targetEdges and sourceEdges
 *  2. Remove that edge from their targetEdges/sourceEdges
 * @param {Object} nodes - a map for nodes, with ID as key and node object as value
 * @param {Object} edges - the edge map in redux store
 * @param {String} deletedEdgeId - the ID of the edge being deleted
 * @return {Object} - The updated node map, with targetEdges and sourceEdges updated
 */
export function deleteEdgesFromNodes(nodes, edges, deletedEdgeId) {
  const sourceNode = nodes[edges[deletedEdgeId]?.source];
  const targetNode = nodes[edges[deletedEdgeId]?.target];

  if (!sourceNode || !targetNode) return nodes;

  // Remove the deleted edge from sourceNode's sourceEdges, and from targetNode's targetEdges,
  // but leave the rest intact
  return {
    ...nodes,
    [sourceNode.id]: {
      ...sourceNode,
      sourceEdges: sourceNode.sourceEdges.filter(
        sourceEdge => sourceEdge.id !== deletedEdgeId,
      ),
    },
    [targetNode.id]: {
      ...targetNode,
      targetEdges: targetNode.targetEdges.filter(
        targetEdge => targetEdge.id !== deletedEdgeId,
      ),
    },
  };
}

/**
 * Given the edges in the redux cache, remove the edge the user just clicked.
 * @param {Object} edges - the edge map in redux store
 * @param {String} deletedEdgeId - the id of the edge/dependency
 * @returns {Object} - the modified edge map
 */
export function deleteEdge(edges, deletedEdgeId) {
  const newEdges = { ...edges };
  delete newEdges[deletedEdgeId];
  return newEdges;
}
